import { gql } from '@apollo/client/core';

/**
 * Тонкий фрагмент для списков (лента витрины, панель возврата): без
 * refund-графа и audit_logs, которые нужны только на детальном/checkout-экране.
 * UI всегда читает названия, цены, клиента и сотрудников из Order-снапшота.
 */
export const ORDER_LIST_FIELDS = gql`
  fragment OrderActorFields on OrderActor {
    id
    title
  }

  fragment OrderLineFields on OrderLine {
    id
    booking_id
    catalog_item_id
    catalog_tariff_id
    line_type
    position
    title_snapshot
    duration_minutes_snapshot
    quantity
    unit_price
    gross_amount
    discount_amount
    net_amount
    refunded_amount
  }

  fragment OrderMoneyPartFields on OrderMoneyPart {
    id
    payment_id
    method
    amount
    refundable_amount
    captured_at
    captured_by_employee {
      ...OrderActorFields
    }
  }

  fragment OrderListFields on Order {
    id
    organization_id
    location_id
    client_id
    booking_group_id
    revision
    status
    source
    currency
    customer_snapshot {
      id
      type
      first_name
      last_name
      patronymic_name
      phone_masked
      email_masked
    }
    gross_amount
    discount_amount
    total_amount
    paid_amount
    refunded_amount
    fiscal_receipt_state
    unowned_marking_codes
    acquiring_instrument
    remaining_amount
    advance_amount
    version
    paid_at
    cancelled_at
    created_at
    updated_at
    location {
      id
      title
    }
    created_by_employee {
      ...OrderActorFields
    }
    paid_by_employee {
      ...OrderActorFields
    }
    cancelled_by_employee {
      ...OrderActorFields
    }
    lines {
      ...OrderLineFields
    }
    adjustments {
      id
      order_line_id
      adjustment_type
      source
      label
      amount
      line_points {
        order_line_id
        points
      }
    }
    money_parts {
      ...OrderMoneyPartFields
    }
  }
`;

/**
 * Полный immutable financial snapshot заказа (детальный/checkout экран):
 * тонкий OrderListFields плюс refund-граф и audit_logs.
 */
export const ORDER_FIELDS = gql`
  ${ORDER_LIST_FIELDS}

  fragment OrderFields on Order {
    ...OrderListFields
    final_receipt_outside_mors
    refunds {
      id
      status
      amount
      reason_code
      reason
      completed_at
      created_by_employee {
        ...OrderActorFields
      }
      lines {
        id
        order_line_id
        quantity
        amount
        order_line {
          ...OrderLineFields
        }
      }
      money_parts {
        money_part_id
        method
        amount
      }
    }
    fiscal_operations {
      timezone
      id
      location_id
      order_id
      refund_id
      operation_type
      status
      bank_payment_status
      kkm_command_status
      failure_kind
      error_code
      error_message
      warning_code
      payment_captured_at
      fiscal_drive_number_masked
      fiscal_document_number
      fiscal_document_sign
      shift_number
      receipt_number
      receipt_url
      fiscalized_at
      prepared_at
      started_at
      reconciliation_required_at
      failed_at
      completed_at
      workstation {
        id
        name
      }
    }
    audit_logs {
      id
      action
      actor_employee_id
      old_values
      new_values
      metadata
      created_at
      actor_employee {
        ...OrderActorFields
      }
    }
  }
`;

export const ORDER_CHECKOUT_CONTEXT_FIELDS = gql`
  ${ORDER_FIELDS}

  fragment OrderCheckoutContextFields on OrderCheckoutContext {
    advances {
      id
      amount
      available_amount
      fiscal_scheme
      created_at
      receipt_status
    }
    open_payment_links {
      id
      payment_id
      amount
      inv_id
      status
      expires_at
      url
    }
    payment_link_availability {
      available
      reason_code
    }
    pay_url
    send_targets {
      conversation_id
      provider
    }
    advance_total
    remaining_to_pay
    overpayment_to_refund
    final_receipt_required
    order {
      ...OrderFields
    }
    source_state
    allowed_payment_methods
    fiscalization_required
    shift_required
    shift_state
    shift_blocker_reason
    shift_expires_at
    fiscalization_readiness {
      setting_enabled
      effective_enabled
      kkt_status
      terminal_status
      setting {
        id
        expected_kkt_inn_masked
        cashier_display_name
        config_version
      }
      active_workstation {
        id
        workstation_id
        active
        kkt_status
        terminal_status
        kkt_verified_at
        terminal_verified_at
      }
      last_workstation {
        id
        workstation_id
        active
        kkt_status
        terminal_status
        kkt_verified_at
        terminal_verified_at
      }
    }
    active_fiscal_operation {
      timezone
      id
      order_id
      location_id
      operation_type
      status
      bank_payment_status
      kkm_command_status
      failure_kind
      error_code
      error_message
      warning_code
      payment_captured_at
      fiscal_drive_number_masked
      fiscal_document_number
      fiscal_document_sign
      shift_number
      receipt_number
      receipt_url
      fiscalized_at
      completed_at
    }
    loyalty {
      available_balance
      max_redeemable_points
    }
    loyalty_program_active
  }
`;

export const ORDER_REFUND_APPROVAL_FIELDS = gql`
  fragment OrderRefundApprovalFields on OrderRefundApproval {
    id
    order_id
    location_id
    status
    kind
    transfer_amount
    requested_at
    request_comment
    resolved_at
    resolve_comment
    expires_at
    location {
      id
      title
    }
    requested_by_employee {
      id
      title
    }
    resolved_by_employee {
      id
      title
    }
    order {
      id
      status
      total_amount
      refunded_amount
      created_at
      customer_snapshot {
        id
        type
        first_name
        last_name
        patronymic_name
        phone_masked
        email_masked
      }
    }
  }
`;
